import { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// Default configuration for when no msp parameter is provided
const defaultLayout = {
  favicon: "/logo.png",
  logo: {
    img: "/logotrans.png",
    size: 150,
    label: "Breacher.ai"
  },
  color: {
    buttons: "#22FFAF",
    text: "#000000",
    hover: "#1DE9A3"
  },
};

const MSPMap = {
  protect2020: {
    favicon: "/icon2020.png",
    logo: {
      img: "/project2020_logo_white.png",
      size: 350,
      label: ""
    },
    color: {
      buttons: "#EF3C39",
      text: "#FFFFFF",
      hover: "#FF0703"
    },
  },
};

// Define the shape of the context
interface LayoutContextType {
  layout: typeof defaultLayout;
  account: string | undefined;
  msp: string | undefined;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

// Custom hook to access the context
export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};

// Layout provider component
export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { account, msp } = useParams();
  const [layout, setLayout] = useState(MSPMap[msp as keyof typeof MSPMap] || defaultLayout);

  useEffect(() => {
    // Update layout if msp changes
    setLayout(MSPMap[msp as keyof typeof MSPMap] || defaultLayout);
  }, [msp]);

  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = layout.favicon;
  }, [layout.favicon]);

  return (
    <LayoutContext.Provider value={{ layout, account, msp }}>
      {children}
    </LayoutContext.Provider>
  );
};